import { useState } from 'react';
import { CHATBOOKS_ENV } from '../../config/apiConfig';
import {
  ChatbooksAuthAPI,
  ChatbooksAuthResult,
  GOOGLE_SINGLE_SIGN_ON_TYPE,
} from '../../utils/chatbooksAuth';
import { parseGoogleIdToken } from '../../utils/googleIdToken';
import { useGoogleSso } from '../contexts/GoogleSsoContext';

export type FlowAuthMethod = 'email' | 'google';

interface UseFlowAuthOptions {
  onAuthenticated: (
    result: ChatbooksAuthResult,
    authMethod: FlowAuthMethod,
  ) => void | Promise<void>;
}

export function useFlowAuth({ onAuthenticated }: UseFlowAuthOptions) {
  const [isBusy, setIsBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  const [busyAuthMethod, setBusyAuthMethod] = useState<FlowAuthMethod | null>(null);
  const [externalError, setExternalError] = useState<string | null>(null);
  const { signInWithGoogle, isConfigured: isGoogleConfigured } = useGoogleSso();

  const handleSignUp = async (data: {
    fullName: string;
    email: string;
    password: string;
  }): Promise<{ error?: string } | void> => {
    setExternalError(null);
    setBusyMessage('Creating your Chatbooks account');
    setBusyAuthMethod('email');
    setIsBusy(true);

    try {
      const authResult = await ChatbooksAuthAPI.createUser(
        data.fullName,
        data.email,
        data.password,
        CHATBOOKS_ENV,
      );
      setBusyMessage('Getting your upload session ready');
      await onAuthenticated(authResult, 'email');
    } catch (err) {
      const message = err instanceof Error ?
        err.message :
        'Something went wrong, please try again';
      return { error: message };
    } finally {
      setBusyAuthMethod(null);
      setIsBusy(false);
    }
  };

  const handleSignIn = async (data: {
    email: string;
    password: string;
  }): Promise<{ error?: string } | void> => {
    setExternalError(null);
    setBusyMessage('Signing you in');
    setBusyAuthMethod('email');
    setIsBusy(true);

    try {
      const authResult = await ChatbooksAuthAPI.logon(
        data.email,
        data.password,
        CHATBOOKS_ENV,
      );
      setBusyMessage('Getting your upload session ready');
      await onAuthenticated(authResult, 'email');
    } catch (err) {
      const message = err instanceof Error ?
        err.message :
        'Invalid email or password.';
      return { error: message };
    } finally {
      setBusyAuthMethod(null);
      setIsBusy(false);
    }
  };

  const handleGoogleSSO = async (): Promise<{ error?: string } | void> => {
    if (!isGoogleConfigured) {
      return { error: 'Google sign-in is not configured for this environment.' };
    }

    setExternalError(null);
    setBusyMessage('Opening Google sign-in');
    setBusyAuthMethod('google');
    setIsBusy(true);

    try {
      const idToken = await signInWithGoogle();
      setBusyMessage('Signing you in with Google');
      const claims = parseGoogleIdToken(idToken);
      const authResult = await ChatbooksAuthAPI.singleSignOn(
        idToken,
        GOOGLE_SINGLE_SIGN_ON_TYPE,
        {
          email: claims.email,
          name: claims.name,
        },
        CHATBOOKS_ENV,
      );
      setBusyMessage('Getting your upload session ready');
      await onAuthenticated(authResult, 'google');
    } catch (err) {
      const message = err instanceof Error ?
        err.message :
        'Google sign-in failed. Please try again.';
      return { error: message };
    } finally {
      setBusyAuthMethod(null);
      setIsBusy(false);
    }
  };

  const handleSSO = (provider: 'google' | 'apple') => {
    if (provider === 'google') {
      void handleGoogleSSO().then((result) => {
        if (result?.error) {
          setExternalError(result.error);
        }
      });
      return;
    }

    setExternalError('Apple sign-in is coming soon.');
  };

  return {
    handleSignUp,
    handleSignIn,
    handleSSO,
    isBusy,
    busyMessage,
    busyAuthMethod,
    externalError,
    setExternalError,
  };
}
